/**
 * OfflineQueue -- Soporte offline-first para Carrito e Inventario.
 *
 * Mision: PANEL-OPS-V2-001 T2
 *
 * Cache de catalogos (productos/insumos) + cola de operaciones pendientes
 * (carrito/inventario) en IndexedDB, sin dependencias externas.
 *
 * Estructura:
 *   DB: nexo_offline
 *   Stores:
 *     - productos_cache (keyPath: id)          index: businessId
 *     - insumos_cache   (keyPath: id)          index: businessId
 *     - pending_ops     (keyPath: id, autoInc) index: businessId
 *
 * API publica:
 *   OfflineQueue.cacheList(type, businessId, items)
 *   OfflineQueue.getCachedList(type, businessId)
 *   OfflineQueue.enqueue(op)              -> {id}
 *   OfflineQueue.listPending(businessId)  -> [...]
 *   OfflineQueue.remove(id)
 *   OfflineQueue.clearForBusiness(businessId)
 *   OfflineQueue.flush(sender)            -> { sent, failed, remaining }
 *   OfflineQueue.isOnline()
 *
 * El evento custom `nexo-offline-queue-change` se emite cuando la cola
 * cambia (enqueue/remove/flush) — las vistas escuchan para refrescar UI.
 *
 * @module infrastructure/offline/OfflineQueue
 */

const DB_NAME = 'nexo_offline';
const DB_VERSION = 1;
const STORE_PRODUCTOS = 'productos_cache';
const STORE_INSUMOS   = 'insumos_cache';
const STORE_PENDING   = 'pending_ops';
const MAX_ATTEMPTS    = 3;

let _dbPromise = null;

function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      reject(new Error('IndexedDB no soportado'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onupgradeneeded = (ev) => {
      const db = ev.target.result;
      if (!db.objectStoreNames.contains(STORE_PRODUCTOS)) {
        const s = db.createObjectStore(STORE_PRODUCTOS, { keyPath: 'id' });
        s.createIndex('businessId', 'businessId', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_INSUMOS)) {
        const s = db.createObjectStore(STORE_INSUMOS, { keyPath: 'id' });
        s.createIndex('businessId', 'businessId', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_PENDING)) {
        const s = db.createObjectStore(STORE_PENDING, { keyPath: 'id', autoIncrement: true });
        s.createIndex('businessId', 'businessId', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
  return _dbPromise;
}

function tx(storeName, mode = 'readonly') {
  return openDb().then(db => db.transaction(storeName, mode).objectStore(storeName));
}

function asPromise(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function emitChange() {
  try { window.dispatchEvent(new CustomEvent('nexo-offline-queue-change')); } catch { /* noop */ }
}

function storeFor(type) {
  if (type === 'productos') return STORE_PRODUCTOS;
  if (type === 'insumos')   return STORE_INSUMOS;
  throw new Error(`Tipo de cache invalido: ${type}`);
}

export const OfflineQueue = {
  isOnline() {
    return typeof navigator !== 'undefined' ? navigator.onLine !== false : true;
  },

  /** Guarda un listado completo (reemplaza las entradas del negocio). */
  async cacheList(type, businessId, items) {
    if (!Array.isArray(items)) return 0;
    try {
      const db = await openDb();
      const t = db.transaction(storeFor(type), 'readwrite');
      const store = t.objectStore(storeFor(type));
      // Purgar las entradas previas del mismo negocio
      const idx = store.index('businessId');
      const cur = idx.openCursor(IDBKeyRange.only(String(businessId)));
      await new Promise((resolve) => {
        cur.onsuccess = (ev) => {
          const c = ev.target.result;
          if (c) { c.delete(); c.continue(); } else resolve();
        };
        cur.onerror = () => resolve();
      });
      let count = 0;
      for (const it of items) {
        const row = { ...it, businessId: String(businessId) };
        if (row.id === undefined || row.id === null || row.id === '') continue;
        store.put(row);
        count++;
      }
      await new Promise((resolve, reject) => {
        t.oncomplete = resolve;
        t.onerror = () => reject(t.error);
        t.onabort = () => reject(t.error);
      });
      return count;
    } catch {
      return 0;
    }
  },

  async getCachedList(type, businessId) {
    try {
      const store = await tx(storeFor(type));
      const idx = store.index('businessId');
      return await asPromise(idx.getAll(IDBKeyRange.only(String(businessId))));
    } catch {
      return [];
    }
  },

  /**
   * Encola una operacion pendiente.
   * op = { tipo: 'carrito'|'inventario', businessId, endpoint, payload }
   */
  async enqueue(op) {
    const record = {
      tipo: op.tipo,
      businessId: String(op.businessId || ''),
      endpoint: op.endpoint,
      payload: op.payload,
      created_at: new Date().toISOString(),
      attempts: 0,
      last_error: null
    };
    try {
      const store = await tx(STORE_PENDING, 'readwrite');
      const id = await asPromise(store.add(record));
      emitChange();
      return { id };
    } catch (err) {
      return { id: null, error: err?.message || 'enqueue_failed' };
    }
  },

  async listPending(businessId) {
    try {
      const store = await tx(STORE_PENDING);
      if (businessId === undefined) {
        return await asPromise(store.getAll());
      }
      const idx = store.index('businessId');
      return await asPromise(idx.getAll(IDBKeyRange.only(String(businessId))));
    } catch {
      return [];
    }
  },

  async remove(id) {
    try {
      const store = await tx(STORE_PENDING, 'readwrite');
      await asPromise(store.delete(id));
      emitChange();
      return true;
    } catch {
      return false;
    }
  },

  /** Limpia cache + cola para un negocio (cambio de businessId). */
  async clearForBusiness(businessId) {
    const bid = String(businessId || '');
    try {
      const db = await openDb();
      const names = [STORE_PRODUCTOS, STORE_INSUMOS, STORE_PENDING];
      const t = db.transaction(names, 'readwrite');
      for (const name of names) {
        const s = t.objectStore(name);
        const idx = s.index('businessId');
        const cur = idx.openCursor(IDBKeyRange.only(bid));
        await new Promise((resolve) => {
          cur.onsuccess = (ev) => {
            const c = ev.target.result;
            if (c) { c.delete(); c.continue(); } else resolve();
          };
          cur.onerror = () => resolve();
        });
      }
      await new Promise((resolve) => { t.oncomplete = resolve; t.onerror = resolve; });
      emitChange();
    } catch { /* noop */ }
  },

  /**
   * Drena la cola secuencialmente llamando a sender({ endpoint, payload }).
   * sender debe devolver una Promise que resuelve a { ok: boolean, error? }.
   * Items con attempts >= MAX_ATTEMPTS se dejan en cola marcados.
   *
   * opts.filter (FIX #2050, politica de antiguedad): (op) => boolean. Items
   * que NO pasan el filtro quedan en cola sin tocar (cuentan en remaining) --
   * permite drenar solo operaciones seguras (p.ej. conteos de inventario de
   * OTRO dia no se reenvian a ciegas: pisarian el consolidado equivocado).
   * Sin filter: comportamiento previo (drena todo), CarritoView intacto.
   */
  async flush(sender, businessId, { filter } = {}) {
    const items = await this.listPending(businessId);
    let sent = 0, failed = 0, remaining = 0;
    for (const it of items) {
      if (typeof filter === 'function' && !filter(it)) { remaining++; continue; }
      if ((it.attempts || 0) >= MAX_ATTEMPTS) { remaining++; continue; }
      try {
        const res = await sender({ endpoint: it.endpoint, payload: it.payload, tipo: it.tipo });
        if (res && res.ok) {
          await this.remove(it.id);
          sent++;
        } else {
          await _bumpAttempts(it.id, res?.error || 'send_failed');
          failed++;
        }
      } catch (err) {
        await _bumpAttempts(it.id, err?.message || 'exception');
        failed++;
      }
    }
    emitChange();
    return { sent, failed, remaining: remaining + failed };
  }
};

async function _bumpAttempts(id, errMsg) {
  try {
    const store = await tx(STORE_PENDING, 'readwrite');
    const cur = await asPromise(store.get(id));
    if (!cur) return;
    cur.attempts = (cur.attempts || 0) + 1;
    cur.last_error = String(errMsg).slice(0, 200);
    await asPromise(store.put(cur));
  } catch { /* noop */ }
}
